import { useState } from 'react'

import { ArrowDownward, ArrowUpward } from '@mui/icons-material'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { Area } from './Area'
import { ChipComponent as Chip } from './chip'
import { Currency } from './Currency'
import { Floor } from './Floor'
import { FloorPlanButton } from './FloorPlanButton'
import { Rooms } from './Rooms'
import { getComparator } from './utils'

export const CardStack = ({ fields, onClick, properties }) => {
  const { t } = useTranslation()
  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState('number')
  const [anchorEl, setAnchorEl] = useState(null)

  const open = Boolean(anchorEl)

  const handleClickSort = (field) => {
    const isAsc = orderBy === field && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(field)
    setAnchorEl(null)
  }

  const handleClickSortButton = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClickToggleSortDirection = () => {
    setOrder(order === 'asc' ? 'desc' : 'asc')
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const buttonLabel = (() => {
    const labels = {
      number: fields?.includes('NUMBER') ? t('Sort by number') : null,
      status: fields?.includes('STATUS') ? t('Sort by status') : null,
      building: fields?.includes('BUILDING') ? t('Sort by building') : null,
      rooms: fields?.includes('ROOMS') ? t('Sort by rooms') : null,
      area: fields?.includes('AREA') ? t('Sort by area') : null,
      floor: fields?.includes('FLOOR') ? t('Sort by floor') : null,
      price: fields?.includes('PRICE') ? t('Sort by price') : null,
      monthlyFee: fields?.includes('MONTHLY_FEE')
        ? t('Sort by monthly fee')
        : null,
    }

    return (
      labels[orderBy] ||
      Object.values(labels).find(Boolean) ||
      t('Sort by number')
    )
  })()

  const sorted = properties?.toSorted?.(getComparator(order, orderBy)) ?? []

  return (
    <Stack spacing={1} alignItems="end">
      <Stack direction="row" alignItems="center">
        <Button
          id="sort-button"
          aria-controls={open ? 'sort-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClickSortButton}
        >
          {buttonLabel}
        </Button>
        <Menu
          id="sort-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'sort-button',
            sx: { width: anchorEl?.offsetWidth },
          }}
        >
          {fields?.includes('NUMBER') && (
            <MenuItem
              selected={orderBy === 'number'}
              onClick={() => handleClickSort('number')}
            >
              {t('Number')}
            </MenuItem>
          )}
          {fields?.includes('STATUS') && (
            <MenuItem
              selected={orderBy === 'status'}
              onClick={() => handleClickSort('status')}
            >
              {t('Status')}
            </MenuItem>
          )}
          {fields?.includes('BUILDING') && (
            <MenuItem
              selected={orderBy === 'building'}
              onClick={() => handleClickSort('building')}
            >
              {t('Building')}
            </MenuItem>
          )}
          {fields?.includes('ROOMS') && (
            <MenuItem
              selected={orderBy === 'rooms'}
              onClick={() => handleClickSort('rooms')}
            >
              {t('Rooms')}
            </MenuItem>
          )}
          {fields?.includes('AREA') && (
            <MenuItem
              selected={orderBy === 'area'}
              onClick={() => handleClickSort('area')}
            >
              {t('Area')}
            </MenuItem>
          )}
          {fields?.includes('FLOOR') && (
            <MenuItem
              selected={orderBy === 'floor'}
              onClick={() => handleClickSort('floor')}
            >
              {t('Floor')}
            </MenuItem>
          )}
          {fields?.includes('PRICE') && (
            <MenuItem
              selected={orderBy === 'price'}
              onClick={() => handleClickSort('price')}
            >
              {t('Price')}
            </MenuItem>
          )}
          {fields?.includes('MONTHLY_FEE') && (
            <MenuItem
              selected={orderBy === 'monthlyFee'}
              onClick={() => handleClickSort('monthlyFee')}
            >
              {t('Monthly fee')}
            </MenuItem>
          )}
        </Menu>
        <IconButton
          aria-label="toggle sort direction"
          size="small"
          color="primary"
          onClick={handleClickToggleSortDirection}
        >
          {order === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
        </IconButton>
      </Stack>
      <Stack spacing={1} sx={{ width: 1 }} divider={<Divider flexItem />}>
        {sorted?.map?.((p) => (
          <Box
            key={p.id}
            sx={[
              {
                p: 2,
              },
              onClick && p.status !== 'UPCOMING' ? { cursor: 'pointer' } : {},
            ]}
            onClick={() => {
              if (onClick && p.status !== 'UPCOMING') onClick(p)
            }}
          >
            <Stack spacing={1}>
              {/* Header section */}
              {(fields?.includes('NUMBER') || fields?.includes('STATUS')) && (
                <Stack direction="row" justifyContent="space-between">
                  {fields?.includes('NUMBER') && (
                    <Typography variant="h6">{p.number}</Typography>
                  )}
                  {fields?.includes('STATUS') && <Chip status={p.status} />}
                </Stack>
              )}

              {/* Inline section */}
              {(fields?.includes('BUILDING') ||
                fields?.includes('ROOMS') ||
                fields?.includes('AREA') ||
                fields?.includes('FLOOR')) && (
                <Stack
                  direction="row"
                  spacing={1}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  {fields?.includes('BUILDING') && (
                    <Typography>{p.building}</Typography>
                  )}
                  {fields?.includes('ROOMS') && <Rooms value={p.rooms} />}
                  {fields?.includes('AREA') && <Area value={p.area} />}
                  {fields?.includes('FLOOR') && <Floor value={p.floor} />}
                </Stack>
              )}

              {/* Row section */}
              {fields?.includes('FLOOR_PLAN_URL') && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography>{t('Floor plan')}</Typography>
                  <FloorPlanButton url={p.floorPlanUrl} />
                </Stack>
              )}
              {fields?.includes('PRICE') && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('Price')}</Typography>
                  <Typography>
                    <Currency value={p.price} />
                  </Typography>
                </Stack>
              )}
              {fields?.includes('MONTHLY_FEE') && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('Monthly fee')}</Typography>
                  <Typography>
                    <Currency value={p.monthlyFee} />
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Stack>
  )
}
